'use client';

import iconCharacter from '@/assets/images/icons/icon-character.png';
import iconComa from '@/assets/images/icons/icon-coma.png';
import FaIcon, { faChevronRight, faXmark } from '@/components/icon/FaIcon/FaIcon';
import ImageView from '@/components/ui/ImageView/ImageView';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import { MaterialFilter, type MaterialFilterTabValue, type Product } from '@/types';
import { getImageLayoutSizes } from '@/utils/nextImage';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './FilterBottomSheet.module.scss';

const CATEGORY_LABEL: Record<MaterialFilterTabValue, string> = {
  [MaterialFilter.ALL]: 'すべて',
  [MaterialFilter.CHARACTER]: 'キャラクター',
  [MaterialFilter.FRAME]: 'コマ',
};

function FilterBottomSheet({
  isOpen,
  onClose,
  products,
  selectedProductId,
  onItemFilterToggle,
  filterTab,
  onTabChange,
  onClear,
}: {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
  selectedProductId: number | null;
  onItemFilterToggle: (productId: number | null) => void;
  filterTab: MaterialFilterTabValue;
  onTabChange: (tab: MaterialFilterTabValue) => void;
  onClear: () => void;
}) {
  const [isItemMenuOpen, setIsItemMenuOpen] = useState(false);
  const originalOverflowRef = useRef('');

  // ローカルstate: 「絞り込む」ボタンを押すまで親に反映しない
  const [localProductId, setLocalProductId] = useState<number | null>(selectedProductId);
  const [localFilterTab, setLocalFilterTab] = useState<MaterialFilterTabValue>(filterTab);

  // モーダルを開くたびに親の値で初期化
  useEffect(() => {
    if (isOpen) {
      setLocalProductId(selectedProductId);
      setLocalFilterTab(filterTab);
      setIsItemMenuOpen(false);
      originalOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = originalOverflowRef.current;
    };
  }, [isOpen, selectedProductId, filterTab]);

  const handleApply = useCallback(() => {
    // アイテムフィルター適用
    if (localProductId !== selectedProductId) {
      onItemFilterToggle(localProductId);
    }
    // コマ種別フィルター適用
    if (localFilterTab !== filterTab) {
      onTabChange(localFilterTab);
    }
    onClose();
  }, [localProductId, localFilterTab, selectedProductId, filterTab, onItemFilterToggle, onTabChange, onClose]);

  const handleClear = useCallback(() => {
    onClear();
    onClose();
  }, [onClear, onClose]);

  const localProductName =
    localProductId !== null ? (products.find((p) => p.id === localProductId)?.name ?? '指定なし') : '指定なし';

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} aria-hidden='true' />
      <dialog open className={styles.sheet} aria-label='絞り込み'>
        {/* ヘッダー */}
        <div className={styles.header}>
          <h3 className={styles.headerTitle}>絞り込み</h3>
          <button type='button' className={styles.closeButton} onClick={onClose} aria-label='閉じる'>
            <FaIcon icon={faXmark} />
          </button>
        </div>

        <hr className={styles.divider} />

        <div className={styles.content}>
          {/* アイテムフィルター */}
          <button type='button' className={styles.filterRow} onClick={() => setIsItemMenuOpen(!isItemMenuOpen)}>
            <span className={styles.filterLabel}>アイテム</span>
            <span className={styles.filterValue}>
              {localProductName}
              <FaIcon icon={faChevronRight} />
            </span>
          </button>
          {isItemMenuOpen && (
            <ul className={styles.subMenu}>
              <li>
                <button
                  type='button'
                  className={`${styles.subMenuItem} ${localProductId === null ? styles.subMenuItemActive : ''}`}
                  onClick={() => setLocalProductId(null)}
                >
                  指定なし
                </button>
              </li>
              {products.map((product) => (
                <li key={product.id}>
                  <button
                    type='button'
                    className={`${styles.subMenuItem} ${localProductId === product.id ? styles.subMenuItemActive : ''}`}
                    onClick={() => setLocalProductId(product.id)}
                  >
                    <span className={styles.subMenuItemImageWrapper}>
                      <NextImageView
                        src={product.imageUrls.iconSub}
                        alt=''
                        fill
                        sizes={getImageLayoutSizes({ sp: '28px', pc: '32px' })}
                        objectFit='contain'
                      />
                    </span>
                    {product.name}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* コマ種別フィルター */}
          <div className={styles.filterSectionHeader}>
            <span className={styles.filterLabel}>コマ種別</span>
            <span className={styles.filterValue}>{CATEGORY_LABEL[localFilterTab]}</span>
          </div>
          <button
            type='button'
            className={`${styles.categoryItem} ${localFilterTab === MaterialFilter.ALL ? styles.categoryItemActive : ''}`}
            onClick={() => setLocalFilterTab(MaterialFilter.ALL)}
          >
            すべて
            <FaIcon icon={faChevronRight} />
          </button>
          <button
            type='button'
            className={`${styles.categoryItem} ${localFilterTab === MaterialFilter.CHARACTER ? styles.categoryItemActive : ''}`}
            onClick={() => setLocalFilterTab(MaterialFilter.CHARACTER)}
          >
            <span className={styles.categoryItemLabel}>
              <span className={styles.categoryItemIconCharacter}>
                <ImageView src={iconCharacter.src} alt='' isFreeSize />
              </span>
              キャラクター
            </span>
            <div className={styles.rightArrowIconContainer}>
              <FaIcon icon={faChevronRight} />
            </div>
          </button>
          <button
            type='button'
            className={`${styles.categoryItem} ${localFilterTab === MaterialFilter.FRAME ? styles.categoryItemActive : ''}`}
            onClick={() => setLocalFilterTab(MaterialFilter.FRAME)}
          >
            <span className={styles.categoryItemLabel}>
              <span className={styles.categoryItemIconComa}>
                <ImageView src={iconComa.src} alt='' isFreeSize />
              </span>
              コマ
            </span>
            <div className={styles.rightArrowIconContainer}>
              <FaIcon icon={faChevronRight} />
            </div>
          </button>
        </div>
        {/* フッターボタン */}
        <div className={styles.footer}>
          <button type='button' className={styles.clearButton} onClick={handleClear}>
            クリア
          </button>
          <button type='button' className={styles.applyButton} onClick={handleApply}>
            絞り込む
          </button>
        </div>
      </dialog>
    </>
  );
}

export default FilterBottomSheet;
